import { useState, useRef, useEffect } from "react";
import queryString from 'query-string';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './App.css'; // Ensure you have basic styling for .debug-overlay
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { fullScreenPlugin } from '@react-pdf-viewer/full-screen';
import { searchPlugin } from '@react-pdf-viewer/search'; // Removed Search from import
import { Oval } from "react-loader-spinner"

import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/full-screen/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';

import { version as pdfjsVersion } from 'pdfjs-dist';
import heic2any from "heic2any";
import axios from "axios";

// Components imported from local files (assuming they exist in your project)
import CsvViewer from "./Components/CsvViewer";
import MicrosoftViewer from "./Components/MicrosoftViewer";
import HtmlViewer from "./Components/HtmlViewer";
import TxtViewer from "./Components/TxtViewer";
import AdobeViewer from "./Components/Adobeviewer"; // Used for non-mobile PDF viewing
import ReactPlayer from "react-player";

export const isIphoneSafari = () => {
  const ua = window.navigator.userAgent || window.navigator.vendor || window.opera;
  return /iPhone|iPod|iPad/i.test(ua) && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua);
};

export const isAndroid = () => /android/i.test(window.navigator.userAgent);

const App = () => {
  const videoRef = useRef(null);
  const params = queryString.parse(window.location.search);
  const page = params.page && !isNaN(params.page) ? Number(params.page) : 0;
  const [convertedUrl, setConvertedUrl] = useState(null);
  const [finalUrl, setFinalUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasSeeked, setHasSeeked] = useState(false);
  const [searchKeywordState, setSearchKeywordState] = useState(params.search ? [params.search] : []);
  const [searchReady, setSearchReady] = useState(false);

  let modifiedUrl = params.url ? decodeURIComponent(params.url) : null;

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const zoomPluginInstance = zoomPlugin();
  const fullScreenPluginInstance = fullScreenPlugin();
  const searchPluginInstance = searchPlugin({
    keyword: searchKeywordState,
  });
  
  const isMobile = isAndroid() || isIphoneSafari();

  /**
   * useEffect hook to resolve and validate the URL.
   * Handles unshortening of 'awv.app' URLs and basic URL format validation.
   */
  useEffect(() => {
    const resolveUrl = async () => {
      if (!modifiedUrl) {
        setErrorMessage("Error: No valid URL provided. Please include a 'url' parameter in the query string.");
        return;
      }
      if (modifiedUrl.startsWith("https://awv.app")) {
        try {
          const resp = await axios.get(`https://coreapi.wvlnth.net/public/urls/unshortern?url=${encodeURIComponent(modifiedUrl)}`);
          modifiedUrl = resp?.data?.objects?.originalUrl || modifiedUrl;
        } catch (err) {
          console.error("Failed to unshorten URL:", err);
        }
      }
      try {
        new URL(modifiedUrl);
        setFinalUrl(modifiedUrl);
      } catch (err) {
        setErrorMessage("Error: Invalid URL format provided. Please ensure the URL is correctly formatted.");
      }
    };
    resolveUrl();
  }, [modifiedUrl]);

  useEffect(() => {
    // Expose a global function to initiate a search in the PDF viewer
    window.searchKeyword = (term) => {
      if (!searchReady) {
        window.ReactNativeWebView?.postMessage('searchNotReady');
        return false;
      }
    
      try {
        if (searchPluginInstance?.highlight) {
          searchPluginInstance.highlight(term).then((matches) => {
            const matchCount = Array.isArray(matches) && matches.length > 0 ? matches.length : 0;
            setSearchKeywordState([term]);
            window.ReactNativeWebView?.postMessage(`searchResult: ${matchCount}`);
          });
          return true;
        }
        return false;
      } catch (e) {
        window.ReactNativeWebView?.postMessage(`searchError:${e.message}`);
        return false;
      }
    };
    // Expose a global function to clear search results in the PDF viewer
    window.clearSearch = () => {
      // Clear the keyword state to remove highlights
      setSearchKeywordState([]);
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage('searchCleared');
      }
      return true;
    };

    // Expose a global function to jump to the next search result
    window.focusNext = () => {
      if (!searchReady) {
        return false;
      }
      // These methods are still available directly on the plugin instance
      if (searchPluginInstance && searchPluginInstance.jumpToNextMatch) {
        searchPluginInstance.jumpToNextMatch();
        return true;
      }
      return false;
    };

    // Expose a global function to jump to the previous search result
    window.focusPrevious = () => {
      if (!searchReady) {
        return false;
      }
      // These methods are still available directly on the plugin instance
      if (searchPluginInstance && searchPluginInstance.jumpToPreviousMatch) {
        searchPluginInstance.jumpToPreviousMatch();
        return true;
      }
      return false;
    };

    // Reinstate the searchPluginInstance.on(...) calls here
    if (searchPluginInstance.on) {
      searchPluginInstance.on('found', (e) => {
        const matchesCount = e.matches.length;
      });
      searchPluginInstance.on('currentHighlightedMatches', (e) => {
        // Filter for matches on the current page that are highlighted
        const highlightedMatchesOnCurrentPage = e.matches.filter(match => match.isHighlighted && match.pageIndex === e.currentPage);

        let currentIndexValue = 0;
        if (highlightedMatchesOnCurrentPage.length > 0) {
            currentIndexValue = e.matches.findIndex(match => match.isHighlighted) + 1;
        }
        if (window.ReactNativeWebView) {
          if (currentIndexValue > 0) {
            window.ReactNativeWebView.postMessage(`currentMatchIndex:${currentIndexValue}`);
          }
        }
      });
    } 
    return () => {
      window.searchKeyword = undefined;
      window.clearSearch = undefined;
      window.focusNext = undefined;
      window.focusPrevious = undefined;

      // Ensure .off() calls are re-added
      if (searchPluginInstance.off) {
        searchPluginInstance.off('found');
        searchPluginInstance.off('currentHighlightedMatches');
      }
    };
  }, [searchPluginInstance, searchReady]); // searchKeywordState is not a dependency here as global functions don't react to its changes

  const handlePageChange = (e) => {
    const pageNo = document.getElementById('pageNumber');
    if (pageNo) pageNo.innerText = e.currentPage + 1;
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(`pageChange:${e.currentPage + 1}`);
    }
  };

  const renderHEIC = (url) => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => heic2any({ blob, toType: "image/jpeg" }))
      .then(conversionResult => {
        const imageUrl = URL.createObjectURL(conversionResult);
        setConvertedUrl(imageUrl);
      })
      .catch(error => console.error("Error converting HEIC to JPEG:", error)); // Keep this error for critical conversion issues
  };

  const renderViewer = () => {
    if (!finalUrl) {
      return errorMessage ? <div>{errorMessage}</div> : <div>	    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
        <Oval
          height={50}
          width={50}
          color="#165bc3"
          secondaryColor="#165bc3"
          strokeWidth={2}
          strokeWidthSecondary={2}
          visible={true}
          ariaLabel="oval-loading"
        />

    </div></div>;
    }

    const urlObject = new URL(finalUrl);
    const pathname = urlObject.pathname;
    const fileExtension = pathname.split('.').pop().toLowerCase();

    const documentExtensions = ['doc', 'docx', 'ppt', 'pptx'];
    const videoExtensions = ['mp4', 'webm', 'avi', 'mov', 'quicktime', 'wmv', 'video/x-msvideo', 'video/quicktime'];
    const imageExtensions = ['jpg', 'jpeg', 'png', 'heic'];

    if (fileExtension === 'pdf') {
      return (
        <>
          <Worker workerUrl={`https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`}>
            {/* Render @react-pdf-viewer/core Viewer for mobile, AdobeViewer for non-mobile (desktop) */}
            {isMobile ? (
              <div className="document-media-container">
                <Viewer
                  plugins={[
                    pageNavigationPluginInstance,
                    zoomPluginInstance,
                    fullScreenPluginInstance,
                    searchPluginInstance,
                  ]}
                  fileUrl={finalUrl}
                  defaultScale={window.outerWidth < 1024 ? SpecialZoomLevel.PageFit : SpecialZoomLevel.ActualSize}
                  initialPage={page - 1}
                  onPageChange={handlePageChange}
                  onDocumentLoad={() => {
                    setSearchReady(true);
                    if (params.search && params.search.length > 0) {
                      setTimeout(() => {
                        window.searchKeyword(params.search); 
                      }, 300);
                    }
                  }}
                  onError={(error) => {
                      setErrorMessage("Failed to load PDF. Try again.");
                      if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(`pdfError:${error.message || 'Unknown PDF error'}`);
                      }
                  }}
                />
              </div>
            ) : (
              <AdobeViewer url={finalUrl} />
            )}
          </Worker>
        </>
      );
    } else if (videoExtensions.includes(fileExtension)) {
      return (
        <div className="page">
          <ReactPlayer
            ref={videoRef}
            url={finalUrl}
            controls
            width="100%"
            height="100vh"
            playing={!!(params.autoplay || params.play)}
            config={{
              file: {
                forceVideo: true,
                attributes: {
                  controlsList: 'nodownload'
                }
              }
            }}
            onReady={() => {
              const startSeconds = Number(params.page || params.t || params.start || 0);
              if (videoRef.current && startSeconds > 0 && !hasSeeked) {
                videoRef.current.seekTo(startSeconds, 'seconds');
                setHasSeeked(true);
              }
            }}
            onError={(e) => {
              console.error("Video error:", e);
              setErrorMessage("This browser does not support MOV files. Try Firefox or convert to MP4.");
            }}
          />
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
      );
    } else if (documentExtensions.includes(fileExtension)) {
      return <MicrosoftViewer modifiedUrl={finalUrl} page={page} />;
    } else if (imageExtensions.includes(fileExtension)) {
      if (fileExtension === 'heic') {
        renderHEIC(finalUrl);
        return <img className="images-and-video" src={convertedUrl} alt="Document" />;
      }
      return <img className="images-and-video" src={decodeURI(finalUrl)} alt="Document" />;
    } else if (fileExtension === 'csv') {
      return <CsvViewer csvUrl={finalUrl} />;
    } else if (['html', 'htm'].includes(fileExtension)) {
      return <HtmlViewer url={finalUrl} />;
    } else if (fileExtension === 'txt') {
      return <TxtViewer modifiedUrl={finalUrl} />;
    } else {
      return <div>Error: Unsupported file format</div>;
    }
  };

  return (
    <div className="container">
      {renderViewer()}
    </div>
  );
};

export default App;
